import {SidePanel} from '@geomatico/geocomponents';
import type {SxProps} from '@mui/material';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import {type FC, type ReactElement, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Header from '@/components/Header';
import theme from '../theme';

const DRAWER_WIDTH = 450;

// Places content below the mobile Header, following MUI's responsive toolbar heights
const belowHeaderSx = () => {
  const {minHeight, ...toolbarMediaQueries} = theme().mixins.toolbar as {minHeight: number} & Record<
    string,
    {minHeight: number}
  >;
  const offset = (height: number) => ({top: height, height: `calc(100dvh - ${height}px)`});
  return {
    ...offset(minHeight),
    ...Object.fromEntries(
      Object.entries(toolbarMediaQueries).map(([query, value]) => [query, offset(value.minHeight)]),
    ),
  };
};

export type LayoutProps = {
  mainContent: ReactElement;
  sidePanelContent?: ReactElement;
  // On mobile, the side panel closes when this value changes to a truthy one (e.g. the item selected in it)
  closeSidePanelOn?: unknown;
};

const Layout: FC<LayoutProps> = ({mainContent, sidePanelContent, closeSidePanelOn}) => {
  const widescreen = useMediaQuery(theme().breakpoints.up('sm'), {noSsr: true});
  const [isMobileSidePanelOpen, setIsMobileSidePanelOpen] = useState(false);
  const navigate = useNavigate();
  const {lang} = useParams();

  const goHome = () => navigate(`/${lang}/`);

  const toggleSidePanel = () => setIsMobileSidePanelOpen(!isMobileSidePanelOpen);

  useEffect(() => {
    if (!widescreen && closeSidePanelOn) {
      setIsMobileSidePanelOpen(false);
    }
  }, [closeSidePanelOn, widescreen]);

  const isSidePanelOpen = !!sidePanelContent && (widescreen || isMobileSidePanelOpen);

  const sidePanelSx = {
    // On mobile, SidePanel already leaves room for the Header by default
    '& .MuiPaper-root': widescreen ? {top: 0, height: '100%', p: 0} : {p: 0},
  };

  const mainSx: SxProps = {
    position: 'absolute',
    right: 0,
    left: isSidePanelOpen && widescreen ? DRAWER_WIDTH : 0,
    padding: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...(widescreen ? {top: 0, height: '100dvh'} : belowHeaderSx()),
  };

  return (
    <>
      {!widescreen && <Header onStartIconClick={sidePanelContent && toggleSidePanel} onHomeClick={goHome} />}
      {isSidePanelOpen && (
        <SidePanel
          drawerWidth={widescreen ? `${DRAWER_WIDTH}px` : '100vw'}
          anchor="left"
          isOpen={isSidePanelOpen}
          onClose={toggleSidePanel}
          widescreen={widescreen}
          sx={sidePanelSx}
        >
          {sidePanelContent}
        </SidePanel>
      )}
      <Box sx={mainSx}>{mainContent}</Box>
    </>
  );
};

export default Layout;
